import keys from '@debbie/common/dist/cache/keys.js';
import { AlternativeContactType, PrivateStarCycle, } from '@debbie/common/dist/customers/index.js';
import { FeatureFlag } from '@debbie/common/dist/feature-flags/index.js';
import { Site } from '@debbie/common/dist/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as customersAPI from '@debbie/cortex/dist/api/customers.js';
import { cosmoV2Entity } from '@debbie/cortex/dist/cosmoV2/index.js';
import { customersEntity, ensureCustomerLoaded, } from '@debbie/cortex/dist/customers/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { useVariation } from '../../feature-flags/index.js';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import AllLanguagesInput from '../AllLanguagesInput.js';
import Badge from '../Badge.js';
import { Col, Row } from '../Grid/index.js';
import { AlignLeft } from '../Icons/index.js';
import Select from '../Select.js';
import UserAddress from './UserEntites/UserAddress.js';
import UserBirthdate from './UserEntites/UserBirthdate.js';
import UserCompanies from './UserEntites/UserCompanies.js';
import UserCompanyRelations from './UserEntites/UserCompanyRelations.js';
import UserCourt from './UserEntites/UserCourt.js';
import UserEmail from './UserEntites/UserEmail.js';
import UserInfoEntity from './UserEntites/UserInfoEntity.js';
import UserPhone from './UserEntites/UserPhone.js';
import UserRegistryIdentifier from './UserEntites/UserRegistryIdentifier.js';
import UserRkiRegistrations from './UserEntites/UserRkiRegistrations.js';
import UserInfoHeader from './UserInfoHeader.js';
const UserInfo = (props) => {
    const { user, customer } = props;
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const tenantId = useTenantId();
    const hasInternationalEinvoice = useVariation(FeatureFlag.E_INVOICE_INTERNATIONAL);
    const details = props.user.details;
    const [showDescription, setShowDescription] = useState(!!details.description);
    // Throws on failure so the child components' own mutations see the error
    const patchUser = async (details) => {
        if (!tenantId) {
            return;
        }
        await customersAPI.patchUser(customer.customerId, user.userId, {
            details,
        });
        q.invalidate(keys.write.modify(customersEntity, customer.customerId));
        if (details.birthdate !== undefined) {
            // asset ownership is matched on birthdate, so assets must refetch
            q.invalidate(keys.write.create(cosmoV2Entity));
        }
        await ensureCustomerLoaded(customer.customerId);
    };
    const patchUserMutation = useMutation(patchUser, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.party);
        },
    });
    const patchUserLangMutation = useMutation(async (newLang) => {
        await customersAPI.patchUser(customer.customerId, user.userId, {
            lang: newLang,
        });
        q.invalidate(keys.write.modify(customersEntity, customer.customerId));
        await ensureCustomerLoaded(customer.customerId);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.party);
        },
    });
    if (!tenantId) {
        return null;
    }
    return (React.createElement(UserInfoStyled, { onClick: (e) => {
            e.stopPropagation();
        } },
        !showDescription && (React.createElement("div", { style: {
                float: 'right',
                cursor: 'pointer',
                color: THEME.COLOR_ACCENT,
                fontSize: '0.7em',
                fontWeight: 'bold',
            }, onClick: () => {
                setShowDescription(!showDescription);
            } },
            lang.debtorInfo.addDescription,
            ' ',
            React.createElement(AlignLeft, { style: { margin: '0.2em 0 -0.2em 0' } }))),
        React.createElement(UserInfoHeader, { user: user, customer: customer }),
        details && (React.createElement(Row, null,
            React.createElement(Col, { width: "12" },
                details.movedAbroad &&
                    details.movedAbroad[0] &&
                    details.status?.stage === PrivateStarCycle.GoneAway && (React.createElement("p", null,
                    React.createElement(Badge, { theme: '#000' },
                        React.createElement("b", null,
                            lang.debtorInfo.movedTo,
                            ":",
                            ' ',
                            details.movedAbroad.join(', '))))),
                React.createElement("br", null),
                React.createElement(AllLanguagesInput, { disabled: site !== Site.Caseworker, language: user.lang, onChange: (newLang) => patchUserLangMutation.mutate(newLang) }),
                React.createElement("br", null),
                details.type && (React.createElement(Select, { value: details.type, onChange: (e) => patchUserMutation.mutate({
                        type: e.target.value,
                    }) }, Object.values(AlternativeContactType).map((type) => (React.createElement("option", { key: type, value: type }, lang.alternativeContactTypes[type]))))),
                showDescription && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.description, value: details.description, hist: details.descriptionHist, onChange: (val) => {
                        const trimmedDescription = val.trim().replace(/\s\s+/g, ' ');
                        return patchUserMutation.mutate({
                            description: trimmedDescription || null,
                        });
                    } })),
                React.createElement(UserInfoEntity, { title: lang.debtorInfo.name, value: details.name, hist: details.nameHist, onChange: (val) => {
                        const trimmedName = val.trim().replace(/\s\s+/g, ' ');
                        return patchUserMutation.mutate({
                            name: trimmedName,
                        });
                    } }),
                React.createElement(UserEmail, { user: user, patchUser: patchUser }),
                React.createElement(UserPhone, { user: user, patchUser: patchUser }),
                React.createElement(UserAddress, { user: user, patchUser: patchUser }),
                user.relation === 'alternative-contact' && (React.createElement(UserInfoEntity, { title: lang.customers.alternativeContactReferenceId, value: details.alternativeContactReferenceId, onChange: (val) => {
                        const trimmedId = val.trim().replace(/\s\s+/g, ' ');
                        return patchUserMutation.mutate({
                            alternativeContactReferenceId: trimmedId || null,
                        });
                    } })),
                details.guardian?.status && details.guardian.name && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.guardianName, value: details.guardian.name })),
                details.guardian?.status && details.guardian.address && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.guardianAddress, value: details.guardian.address })),
                details.guardian?.status && details.guardian.startDate && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.guardianStartDate, value: details.guardian.startDate })),
                React.createElement(UserCourt, { user: user, patchUser: patchUser }),
                React.createElement(UserBirthdate, { customer: customer, user: user, patchUser: patchUser }),
                customer.isCompany && user.relation !== 'alternative-contact' && (React.createElement(UserInfoEntity, { title: "CVR", value: details.cvr, onChange: (val) => patchUserMutation.mutate({
                        cvr: val || null,
                    }) })),
                !customer.isCompany && user.relation !== 'alternative-contact' && (React.createElement(UserInfoEntity, { title: "CPR", value: details.cpr, onChange: (val) => patchUserMutation.mutate({
                        cpr: val || null,
                    }) })),
                !customer.isCompany && details.status && details.status.stage && (React.createElement(UserInfoEntity, { title: "CPR-status", value: lang.debtorInfo.status[details.status.stage], hist: details.statusHist, renderValue: (val) => {
                        if (typeof val === 'string') {
                            return lang.debtorInfo.status[val] ?? val;
                        }
                        return val && val.stage
                            ? lang.debtorInfo.status[val.stage]
                            : details.status &&
                                lang.debtorInfo.status[details.status.stage];
                    } })),
                details.ean && React.createElement(UserInfoEntity, { title: "EAN", value: details.ean }),
                hasInternationalEinvoice && customer.isCompany && (React.createElement(UserRegistryIdentifier, { user: user, patchUser: patchUser })),
                details.virksomhedsform && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.companyForm, value: details.virksomhedsform })),
                details.industry && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.industry, value: details.industry })),
                details.employees && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.employees, value: details.employees })),
                customer.isCompany && !details.status && details.companyStatus && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.companyStatus, value: details.companyStatus, hist: details.companyStatusHist })),
                customer.isCompany &&
                    !details.status &&
                    !details.companyStatus && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.companyStatus, value: details.aksKOBStatusInitial &&
                        lang.debtorInfo.status[details.aksKOBStatusInitial], hist: details.aksKOBStatusInitialHist })),
                customer.isCompany && details.status?.stage && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.companyStatus, value: lang.debtorInfo.status[details.status.stage], hist: (details.statusHist || []).concat(details.companyStatusHist ? details.companyStatusHist : []), renderValue: (val) => {
                        return val && val.stage
                            ? lang.debtorInfo.status[val.stage]
                            : typeof val === 'string'
                                ? val
                                : details.status &&
                                    lang.debtorInfo.status[details.status.stage];
                    } })),
                customer.isCompany && details.dissolvedStatuses && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.dissolutionStatus, value: lang.debtorInfo.dissolvedStatus[details.dissolvedStatuses.status], hist: details.dissolvedStatuses.data?.map((status) => {
                        return {
                            value: lang.debtorInfo.dissolvedStatus[status.value],
                            source: status.source,
                            createdAt: status.createdAt ??
                                moment(status.oploesningsStatusStartDato + ' 12:00:00', 'DDMMYYYY HH:mm:ss'),
                        };
                    }) })),
                details.website && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.website, value: details.website })),
                details.stiftelsesdato && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.dateOfFoundation, value: details.stiftelsesdato, renderValue: () => moment(details.stiftelsesdato).format(THEME.DATE_FORMAT) })),
                details.antalPenheder && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.quantifyOfPUnits, value: details.antalPenheder })),
                details.tegningsregel && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.powerToBind, value: details.tegningsregel })),
                details.reklamebeskyttet && (React.createElement(UserInfoEntity, { title: lang.debtorInfo.adProtected, value: details.reklamebeskyttet })),
                props.user.details.rkiRegistrations && (React.createElement(UserRkiRegistrations, { user: user })),
                ((details.companyRelations && !!details.companyRelations.length) ||
                    (details.companies && details.companies[0])) && (React.createElement("h2", null, lang.debtorInfo.companyRelations)),
                React.createElement(UserCompanies, { companies: details.companies }),
                customer.isCompany && (React.createElement(React.Fragment, null,
                    React.createElement("br", null),
                    React.createElement(UserCompanyRelations, { companyRelations: details.companyRelations }))))))));
};
export default UserInfo;
const UserInfoStyled = styled.div `
  h3 {
    margin: 0;
    small {
      font-size: 0.7em;
      font-weight: normal;
    }

    & > span {
      font-size: 0.55em;
      font-weight: normal;

      svg {
        margin: 5px 0 -1px 0;
      }
    }
  }
  cursor: default;
`;
//# sourceMappingURL=UserInfo.js.map